import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@welldev-group/rcl-typography';
import InstantTicketWrapper from 'sharedWebpack/InstantTicketWrapper';
import FlexiblePaymentTag from 'sharedWebpack/FlexiblePaymentTag';
import MatrixCard from '../MatrixCard';
import MatrixContext from '../../contexts/MatrixContext';
import WELLTRAVEL from '../../../../lib/helpers/welltravel_channel';
import './matrix-supplier-card.styles.scss';

const MatrixSupplierCard = props => {
  const {
    channelName,
    sourceChannels,
    channelIdentifier,
    instantTicketSupported,
    payLaterSupported,
  } = props;
  const { matrixRowHeaderCardWidth, suppliers } = useContext(MatrixContext);
  const isWellTravelChannel = channelName === WELLTRAVEL;

  const supplier = useMemo(() => {
    const supplierInfo = suppliers[channelName] || {};

    if (isWellTravelChannel && sourceChannels?.length === 1) {
      return supplierInfo[sourceChannels[0]] || supplierInfo;
    } else if (isWellTravelChannel && sourceChannels?.length > 1) {
      return sourceChannels.reduce((acc, channel) => {
        const channelData = supplierInfo[channel] || {};
        Object.entries(channelData).forEach(([key, value]) => {
          acc[key] = Boolean(acc[key]) || Boolean(value);
        });
        return acc;
      }, {});
    }

    return supplierInfo;
  }, [channelName, sourceChannels, isWellTravelChannel]);

  const SupplierLogo = supplier.logo;
  const {
    supportManageBooking,
    supportNdcBooking,
    supportCreateQuote,
    supportFlexibleCancelation,
    supportIssueTicket,
    supportVoidTicket,
    supportRefundTicket,
    supportChangeBooking,
    supportAncillaryServices,
    supportFlightBooking,
  } = supplier;

  return (
    <MatrixCard
      className="matrix-supplier-card"
      width={matrixRowHeaderCardWidth}
      isClickable={false}
    >
      <div className="matrix-supplier-card__channel-logo">
        <img src={SupplierLogo} alt="icon" />
      </div>
      {!isWellTravelChannel && (
        <Text
          className="matrix-supplier-card__channel"
          level={3}
          weight="bold"
        >{`${I18n.t(channelName, { scope: 'channels' })} ${channelIdentifier}`}</Text>
      )}
      {payLaterSupported && <FlexiblePaymentTag matrixCard={true} />}
      <div className="matrix-supplier-card__instant-ticket-tag">
        {instantTicketSupported && <InstantTicketWrapper />}
      </div>
      <ul className="matrix-supplier-card__facilities">
        {supportManageBooking && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.manage_booking')}
            </Text>
          </li>
        )}
        {supportNdcBooking && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.ndc_booking')}
            </Text>
          </li>
        )}
        {supportCreateQuote && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.create_quote')}
            </Text>
          </li>
        )}
        {supportFlexibleCancelation && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.flexible_cancellation')}
            </Text>
          </li>
        )}
        {supportIssueTicket && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.issue_ticket')}
            </Text>
          </li>
        )}
        {supportVoidTicket && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.void_ticket')}
            </Text>
          </li>
        )}
        {supportRefundTicket && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.refund_ticket')}
            </Text>
          </li>
        )}
        {supportChangeBooking && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.change_booking')}
            </Text>
          </li>
        )}
        {supportFlightBooking && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.flight_booking')}
            </Text>
          </li>
        )}
        {supportAncillaryServices && (
          <li className="matrix-supplier-card__facilities-item">
            <span className="matrix-supplier-card__facilities-item-bullet">
              <span className="matrix-supplier-card__facilities-item-bullet-dot" />
            </span>
            <Text level={2} weight="normal">
              {I18n.t('components.ibe.results.supplier_features.ancillary_services')}
            </Text>
          </li>
        )}
      </ul>
    </MatrixCard>
  );
};

MatrixSupplierCard.propTypes = {
  channelName: PropTypes.string.isRequired,
  channelIdentifier: PropTypes.string.isRequired,
  instantTicketSupported: PropTypes.string.isRequired,
  payLaterSupported: PropTypes.string.isRequired,
  sourceChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default MatrixSupplierCard;
